import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, X, ChevronRight } from 'lucide-react';
import ConfirmModal from '../common/ConfirmModal';

const OrderStatusProgression = ({ currentStatus, onStatusChange, disabled = false, isSimple = false }) => {
  const { t } = useTranslation('wholesaler');
  const [confirmAction, setConfirmAction] = useState(null);
  const [deliveryDate, setDeliveryDate] = useState('');

  const statusFlow = isSimple
    ? [
        { key: 'pending', label: t('orders.simpleOrders.placed', 'Καταχωρημένη'), icon: '⏳', color: 'yellow' },
        { key: 'delivered', label: t('orders.simpleOrders.completed', 'Ολοκληρωμένη'), icon: '✅', color: 'green' }
      ]
    : [
        { key: 'pending', label: t('orders.status.pending', 'Εκκρεμής'), icon: '⏳', color: 'yellow' },
        { key: 'confirmed', label: t('orders.status.confirmed', 'Επιβεβαιωμένη'), icon: '✓', color: 'blue' },
        { key: 'processing', label: t('orders.status.processing', 'Σε Προετοιμασία'), icon: '⚙️', color: 'blue' },
        { key: 'shipped', label: t('orders.status.shipped', 'Σε Μεταφορά'), icon: '🚚', color: 'purple' },
        { key: 'delivered', label: t('orders.status.delivered', 'Παραδόθηκε'), icon: '✅', color: 'green' }
      ];

  const getCurrentIndex = () => {
    if (currentStatus === 'cancelled') return -1;
    return statusFlow.findIndex(s => s.key === currentStatus);
  };

  const getNextStatus = () => {
    const currentIndex = getCurrentIndex();
    if (currentIndex === -1 || currentIndex >= statusFlow.length - 1) return null;
    return statusFlow[currentIndex + 1];
  };

  const handleNextStatus = () => {
    const nextStatus = getNextStatus();
    if (nextStatus && !disabled) {
      const isAccepting = currentStatus === 'pending' && nextStatus.key === 'confirmed';
      setDeliveryDate('');
      setConfirmAction({
        type: 'move',
        nextStatusKey: nextStatus.key,
        title: t('orders.statusProgression.confirmMoveTitle', 'Move Order Status'),
        message: t('orders.statusProgression.confirmMove', 'Move order to "{{status}}"?', { status: nextStatus.label }),
        icon: 'info',
        confirmLabel: t('orders.statusProgression.confirmMoveButton', 'Move'),
        confirmColor: 'blue',
        allowDeliveryDate: isAccepting,
      });
    }
  };

  const handleConfirmMove = () => {
    if (!confirmAction) return;
    const payload = deliveryDate
      ? { confirmedDate: new Date(deliveryDate).toISOString() }
      : undefined;
    onStatusChange(confirmAction.nextStatusKey, payload);
    setConfirmAction(null);
    setDeliveryDate('');
  };

  const handleCancel = () => {
    if (currentStatus === 'cancelled') return;
    setConfirmAction({
      type: 'cancel',
      title: t('orders.statusProgression.confirmCancelTitle', 'Cancel Order'),
      message: t('orders.statusProgression.confirmCancel', 'Are you sure you want to cancel this order?'),
      icon: 'danger',
      confirmLabel: t('orders.statusProgression.confirmCancelButton', 'Cancel Order'),
      confirmColor: 'red',
      onConfirm: () => { onStatusChange('cancelled'); setConfirmAction(null); }
    });
  };

  const currentIndex = getCurrentIndex();
  const nextStatus = getNextStatus();
  const isCancelled = currentStatus === 'cancelled';

  const getColorClasses = (color) => {
    const colors = {
      yellow: { bg: 'bg-yellow-100', text: 'text-yellow-800', border: 'border-yellow-300' },
      blue: { bg: 'bg-blue-100', text: 'text-blue-800', border: 'border-blue-300' },
      purple: { bg: 'bg-purple-100', text: 'text-purple-800', border: 'border-purple-300' },
      green: { bg: 'bg-green-100', text: 'text-green-800', border: 'border-green-300' },
      red: { bg: 'bg-red-100', text: 'text-red-800', border: 'border-red-300' },
      gray: { bg: 'bg-gray-100', text: 'text-gray-400', border: 'border-gray-200' }
    };
    return colors[color] || colors.gray;
  };

  return (
    <div>
      {/* Status Flow */}
      <div className="flex items-center justify-between mb-4 overflow-x-auto pb-2">
        {statusFlow.map((status, index) => {
          const isCompleted = index < currentIndex;
          const isCurrent = index === currentIndex;
          const isFuture = index > currentIndex;
          const colors = isCancelled 
            ? getColorClasses('gray')
            : isCompleted 
              ? getColorClasses('green')
              : isCurrent
                ? getColorClasses(status.color)
                : getColorClasses('gray');
          
          return (
            <React.Fragment key={status.key}>
              <div className="flex flex-col items-center min-w-[80px]">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center text-lg border-2 ${colors.bg} ${colors.border} ${
                  isCurrent ? 'ring-2 ring-offset-2 ring-blue-400' : ''
                }`}>
                  {isCompleted ? (
                    <Check className={`w-6 h-6 ${colors.text}`} />
                  ) : (
                    <span>{status.icon}</span>
                  )}
                </div>
                <span className={`text-xs mt-1 text-center font-medium ${
                  isCurrent ? 'text-gray-900' : isCompleted ? 'text-green-700' : 'text-gray-400'
                }`}>
                  {status.label}
                </span>
              </div>
              {index < statusFlow.length - 1 && (
                <ChevronRight className={`w-5 h-5 flex-shrink-0 ${
                  isCompleted ? 'text-green-400' : 'text-gray-300'
                }`} />
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* Action Buttons */}
      {!isCancelled && (
        <div className="flex flex-wrap gap-3">
          {nextStatus && (
            <button
              onClick={handleNextStatus}
              disabled={disabled}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              {isSimple
                ? t('orders.simpleOrders.markCompleted', 'Mark Completed')
                : `${t('orders.statusProgression.moveTo', 'Μετακίνηση σε')} ${nextStatus.label}`}
              <ChevronRight className="w-4 h-4" />
            </button>
          )}
          {currentStatus !== 'delivered' && (
            <button
              onClick={handleCancel}
              disabled={disabled}
              className="flex items-center gap-2 px-4 py-2 border-2 border-red-300 text-red-600 rounded-lg hover:bg-red-50 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <X className="w-4 h-4" />
              {t('orders.cancelOrder', 'Ακύρωση Παραγγελίας')}
            </button>
          )}
        </div>
      )}

      {isCancelled && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-center">
          <span className="text-red-700 font-medium">
            {t('orders.status.cancelled', 'Ακυρώθηκε')}
          </span>
        </div>
      )}

      <ConfirmModal
        isOpen={!!confirmAction}
        title={confirmAction?.title}
        message={confirmAction?.message}
        icon={confirmAction?.icon}
        confirmLabel={confirmAction?.confirmLabel}
        confirmColor={confirmAction?.confirmColor}
        onConfirm={confirmAction?.type === 'move' ? handleConfirmMove : confirmAction?.onConfirm}
        onCancel={() => { setConfirmAction(null); setDeliveryDate(''); }}
      >
        {confirmAction?.allowDeliveryDate && (
          <div className="space-y-1">
            <label className="text-xs font-medium text-gray-700">
              {t('orders.statusProgression.confirmedDateLabel', 'Confirmed delivery date (optional)')}
            </label>
            <input
              type="date"
              value={deliveryDate}
              onChange={(e) => setDeliveryDate(e.target.value)}
              onClick={(e) => e.target.showPicker?.()}
              onFocus={(e) => e.target.showPicker?.()}
              className="w-full border border-gray-200 rounded px-2 py-1 text-sm cursor-pointer"
            />
          </div>
        )}
      </ConfirmModal>
    </div>
  );
};

export default OrderStatusProgression;